import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type RefObject } from "react";
import { Film, Music, Pause, Play, RotateCcw, RotateCw, SkipBack, SkipForward } from "lucide-react";
import { ClickPopover, cx, formatBytes } from "@amber/ui";
import type { EvidenceRecord, SpeakerView, TurnView } from "../../contracts/index";
import { checksFact, clock, languageName, laneColor } from "../format";
import { SpeakerContent } from "./Popovers";

export interface PlayerState {
  readonly mediaRef: RefObject<HTMLVideoElement | null>;
  readonly timeMs: number;
  readonly playing: boolean;
  readonly rate: number;
  readonly durationMs: number;
  readonly seek: (ms: number, play?: boolean) => void;
  readonly toggle: () => void;
  readonly setRate: (rate: number) => void;
}

const ZOOMS = [
  { key: 1, label: "Fit" },
  { key: 2, label: "2×" },
  { key: 4, label: "4×" },
] as const;

type Zoom = (typeof ZOOMS)[number]["key"];

function zoomStep(e: globalThis.KeyboardEvent): -1 | 0 | 1 {
  if (e.metaKey || e.ctrlKey || e.altKey) return 0;
  const target = e.target as HTMLElement | null;
  if (target?.closest("input, textarea, select, [contenteditable=true], [role=dialog], [role=menu]")) return 0;
  if (e.key === "+" || e.key === "=") return 1;
  return e.key === "-" || e.key === "−" ? -1 : 0;
}

function useZoomKeys(setZoom: (update: (zoom: Zoom) => Zoom) => void) {
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      const step = zoomStep(e);
      if (step === 0) return;
      e.preventDefault();
      setZoom((zoom) => {
        const at = ZOOMS.findIndex((z) => z.key === zoom) + step;
        return ZOOMS[Math.min(ZOOMS.length - 1, Math.max(0, at))]?.key ?? zoom;
      });
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [setZoom]);
}

function turnAt(turns: readonly TurnView[], ms: number): TurnView | undefined {
  return [...turns].reverse().find((t) => t.start_ms <= ms && ms < t.end_ms + 400) ?? undefined;
}

export function MediaPlayer({
  record,
  player,
  turns,
  speakers,
}: {
  record: EvidenceRecord;
  player: PlayerState;
  turns: readonly TurnView[];
  speakers: readonly SpeakerView[];
}) {
  const media = record.media;
  const current = turnAt(turns, player.timeMs);
  const index = current ? turns.indexOf(current) : -1;
  const next = turns.slice(index + 1).find((t) => t.speaker_id !== current?.speaker_id);
  const nameOf = (id: string | null) => speakers.find((s) => s.id === id)?.display ?? "Unattributed";
  const step = (dir: -1 | 1) => {
    const now = player.timeMs;
    const target =
      dir === 1 ? turns.find((t) => t.start_ms > now + 50) : [...turns].reverse().find((t) => t.start_ms < now - 1500);
    player.seek(target?.start_ms ?? (dir === 1 ? now : 0));
  };
  if (!media) return null;
  return (
    <div className="flex flex-col">
      <div className="relative bg-media-frame">
        <span className="absolute top-2 left-2 z-10 inline-flex items-center gap-1.5 rounded-md bg-media-label px-2 py-1 text-md text-on-media">
          {media.kind === "video" ? <Film size={12} aria-hidden="true" /> : <Music size={12} aria-hidden="true" />}
          Archived copy · plays the captured file
        </span>
        <video
          ref={player.mediaRef}
          src={media.href}
          preload="metadata"
          playsInline
          aria-label={`Archived ${media.kind} of ${record.item.title}`}
          className={cx(
            "mx-auto block w-full bg-media-bg object-contain",
            media.kind === "audio" ? "h-16" : "h-[150px]",
          )}
          onClick={player.toggle}
        />
      </div>
      <input
        type="range"
        min={0}
        max={Math.max(1, player.durationMs)}
        step={100}
        value={player.timeMs}
        aria-label="Position"
        aria-valuetext={`${clock(player.timeMs)} of ${clock(player.durationMs)}`}
        onChange={(e) => {
          player.seek(Number(e.currentTarget.value));
        }}
        className="mx-3 mt-2 accent-[var(--color-link)]"
      />
      <div className="flex items-center gap-1 px-3 py-1.5">
        <IconBtn
          label="Previous turn"
          onClick={() => {
            step(-1);
          }}
        >
          <SkipBack size={15} aria-hidden="true" />
        </IconBtn>
        <IconBtn
          label="Back 10 seconds"
          onClick={() => {
            player.seek(player.timeMs - 10_000);
          }}
        >
          <RotateCcw size={15} aria-hidden="true" />
        </IconBtn>
        <button
          type="button"
          onClick={player.toggle}
          aria-label={player.playing ? "Pause" : "Play"}
          className="mx-1 inline-flex size-9 items-center justify-center rounded-full bg-link text-on-primary"
        >
          {player.playing ? <Pause size={16} aria-hidden="true" /> : <Play size={16} aria-hidden="true" />}
        </button>
        <IconBtn
          label="Forward 10 seconds"
          onClick={() => {
            player.seek(player.timeMs + 10_000);
          }}
        >
          <RotateCw size={15} aria-hidden="true" />
        </IconBtn>
        <IconBtn
          label="Next turn"
          onClick={() => {
            step(1);
          }}
        >
          <SkipForward size={15} aria-hidden="true" />
        </IconBtn>
        <span className="ml-2 font-mono text-ui font-semibold whitespace-nowrap" data-testid="player-time">
          {clock(player.timeMs)} <span className="font-normal text-muted">/ {clock(player.durationMs)}</span>
        </span>
        <span className="ml-3 min-w-0 truncate text-md" aria-live="off">
          {current ? (
            <>
              <span className="font-strong">{nameOf(current.speaker_id)}</span>
              <span className="text-muted">
                {" "}
                · turn {current.n}/{turns.length}
                {next ? ` · next ${nameOf(next.speaker_id)}` : ""}
              </span>
            </>
          ) : null}
        </span>
        <select
          aria-label="Playback speed"
          value={player.rate}
          onChange={(e) => {
            player.setRate(Number(e.currentTarget.value));
          }}
          className="ml-auto h-7 rounded-md border border-border bg-bg px-1 text-md"
        >
          {[0.75, 1, 1.25, 1.5, 2].map((r) => (
            <option key={r} value={r}>
              {r}×
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function IconBtn({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className="inline-flex size-7 items-center justify-center rounded-md text-muted hover:bg-surface-3 hover:text-fg"
    >
      {children}
    </button>
  );
}

function TracksHeader({
  count,
  onAdjustTiming,
  zoom,
  setZoom,
}: {
  count: number;
  onAdjustTiming: (() => void) | null;
  zoom: Zoom;
  setZoom: (zoom: Zoom) => void;
}) {
  return (
    <div className="mb-1.5 flex items-center gap-2">
      <h3 id="tracks-heading" className="m-0 text-xs font-semibold tracking-[0.06em] text-muted uppercase">
        Speaker tracks
      </h3>
      <span className="text-md text-muted">{count} detected · click to play from there</span>
      {onAdjustTiming ? (
        <button
          type="button"
          onClick={onAdjustTiming}
          className="ml-auto h-6 rounded-md border border-divider px-2 text-md"
        >
          Adjust timing
        </button>
      ) : null}
      <div
        role="group"
        aria-label="Zoom"
        className={cx("inline-flex rounded-md border border-divider p-0.5", !onAdjustTiming && "ml-auto")}
      >
        {ZOOMS.map((z) => (
          <button
            key={z.key}
            type="button"
            aria-pressed={zoom === z.key}
            onClick={() => {
              setZoom(z.key);
            }}
            className={cx(
              "h-6 rounded-[5px] px-2 text-md",
              zoom === z.key ? "bg-surface-3 font-semibold" : "text-muted",
            )}
          >
            {z.label}
          </button>
        ))}
      </div>
    </div>
  );
}

interface Lane {
  speaker: SpeakerView | null;
  turns: TurnView[];
}

function LaneLabels({ lanes, player }: { lanes: readonly Lane[]; player: PlayerState }) {
  return (
    <ul className="m-0 w-[150px] flex-none list-none p-0">
      {lanes.map(({ speaker }) => (
        <li key={speaker?.id ?? "none"} className="flex h-[29px] flex-col justify-center pr-2 leading-[1.15]">
          {speaker ? (
            <ClickPopover
              trigger={
                <span
                  className={cx(
                    "block truncate text-ui font-strong",
                    !speaker.name && "text-muted",
                    speaker.lowest_attribution.value !== null &&
                      speaker.lowest_attribution.value < 0.8 &&
                      "underline decoration-dotted underline-offset-2",
                  )}
                >
                  {speaker.display}
                </span>
              }
              label={`${speaker.display}, details`}
            >
              <SpeakerContent
                speaker={speaker}
                onSeek={(ms) => {
                  player.seek(ms);
                }}
              />
            </ClickPopover>
          ) : (
            <span className="truncate text-ui text-muted">Unattributed</span>
          )}
          <span className="truncate text-xs text-muted">
            {speaker
              ? `${speaker.role ? `${speaker.role.value} · ` : ""}${speaker.kind_label} · ${clock(speaker.speaking_ms)}`
              : "no voice assigned"}
          </span>
        </li>
      ))}
    </ul>
  );
}

function tickTransform(i: number, count: number): string {
  if (i === 0) return "none";
  return i === count - 1 ? "translateX(-100%)" : "translateX(-50%)";
}

function TimeTicks({ ticks, pct }: { ticks: readonly number[]; pct: (ms: number) => string }) {
  return (
    <div className="relative h-4 text-2xs text-muted" aria-hidden="true">
      {ticks.map((ms, i) => (
        <span
          key={ms}
          className="absolute top-0.5 font-mono"
          style={{
            left: pct(ms),
            transform: tickTransform(i, ticks.length),
          }}
        >
          {clock(ms)}
        </span>
      ))}
    </div>
  );
}

function OverviewBar({
  total,
  player,
  scroller,
  pct,
}: {
  total: number;
  player: PlayerState;
  scroller: RefObject<HTMLDivElement | null>;
  pct: (ms: number) => string;
}) {
  return (
    <button
      type="button"
      aria-label="Overview: jump to a position"
      className="relative mb-1 ml-[150px] block h-2.5 w-[calc(100%-150px)] rounded-sm bg-surface-2"
      onClick={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        const ms = ((e.clientX - r.left) / r.width) * total;
        player.seek(ms);
        if (scroller.current)
          scroller.current.scrollLeft = (ms / total) * scroller.current.scrollWidth - scroller.current.clientWidth / 2;
      }}
    >
      <span className="absolute inset-y-0 w-0.5 bg-fg" style={{ left: pct(player.timeMs) }} />
    </button>
  );
}

export function SpeakerTracks({
  record,
  player,
  turns,
  speakers,
  selected,
  onSelect,
  onAdjustTiming = null,
}: {
  record: EvidenceRecord;
  player: PlayerState;
  turns: readonly TurnView[];
  speakers: readonly SpeakerView[];
  selected: number | null;
  onSelect: (idx: number) => void;
  onAdjustTiming?: (() => void) | null;
}) {
  const [zoom, setZoom] = useState<Zoom>(1);
  useZoomKeys(setZoom);
  const scroller = useRef<HTMLDivElement>(null);
  const total = Math.max(player.durationMs, record.key_facts.clip.duration_ms ?? 0, turns.at(-1)?.end_ms ?? 0, 1);
  const lanes = useMemo((): Lane[] => {
    const out = speakers.map((s) => ({ speaker: s, turns: turns.filter((t) => t.speaker_id === s.id) }));
    const loose = turns.filter((t) => t.speaker_id === null);
    return loose.length > 0 ? [...out, { speaker: null, turns: loose }] : out;
  }, [speakers, turns]);
  const pct = (ms: number) => `${String((ms / total) * 100)}%`;
  const ticks = Array.from({ length: 7 }, (_, i) => Math.round((total / 6) * i));
  const [active, setActive] = useState<Map<number, number>>(() => new Map());
  const onKey = (e: KeyboardEvent<HTMLDivElement>, lane: number, own: readonly TurnView[], pos: number) => {
    const pick = (p: number) => {
      const t = own[Math.min(own.length - 1, Math.max(0, p))];
      if (t) setActive(new Map(active).set(lane, t.idx));
    };
    if (e.key === "ArrowRight") pick(pos + 1);
    else if (e.key === "ArrowLeft") pick(pos - 1);
    else if (e.key === "Home") pick(0);
    else if (e.key === "End") pick(own.length - 1);
    else if (e.key === "Enter" || e.key === " ") {
      const t = own[pos];
      if (t) {
        onSelect(t.idx);
        player.seek(t.start_ms);
      }
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      document.querySelector<HTMLElement>(`[data-lane="${String(lane + (e.key === "ArrowDown" ? 1 : -1))}"]`)?.focus();
    } else return;
    e.preventDefault();
    e.stopPropagation();
  };
  if (record.speakers_state === "pending" && turns.every((t) => t.speaker_id === null)) {
    return (
      <p className="m-0 border-t border-divider px-3 py-3 text-md text-muted">
        Speaker tracks appear after speakers are identified.
      </p>
    );
  }
  return (
    <section aria-labelledby="tracks-heading" className="border-t border-divider px-3 pt-2 pb-2">
      <TracksHeader count={speakers.length} onAdjustTiming={onAdjustTiming} zoom={zoom} setZoom={setZoom} />
      {zoom > 1 ? <OverviewBar total={total} player={player} scroller={scroller} pct={pct} /> : null}
      <div className="flex">
        <LaneLabels lanes={lanes} player={player} />
        <div ref={scroller} className="min-w-0 flex-1 overflow-x-auto">
          <div className="relative" style={{ width: `${String(zoom * 100)}%` }}>
            {lanes.map(({ speaker, turns: own }, laneIdx) => {
              const activePos = Math.max(
                0,
                own.findIndex((t) => t.idx === (active.get(laneIdx) ?? selected)),
              );
              const activeTurn = own[activePos];
              return (
                <div
                  key={speaker?.id ?? "none"}
                  role="listbox"
                  tabIndex={0}
                  data-lane={laneIdx}
                  aria-label={`${speaker?.display ?? "Unattributed"}: ${String(own.length)} turns. Arrow keys choose a turn, Enter plays from it.`}
                  aria-activedescendant={activeTurn ? `seg-${String(activeTurn.idx)}` : undefined}
                  onKeyDown={(e) => {
                    onKey(e, laneIdx, own, activePos);
                  }}
                  className="group/lane relative h-[29px] border-b border-divider/60 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus"
                >
                  <div className="absolute inset-x-0 top-[7px] h-3.5 rounded-sm bg-surface-2" />
                  {own.map((t, pos) => (
                    <div
                      key={t.idx}
                      id={`seg-${String(t.idx)}`}
                      role="option"
                      aria-selected={selected === t.idx}
                      aria-label={`${speaker?.display ?? "Unattributed"}, ${clock(t.start_ms)} to ${clock(t.end_ms)}${t.attribution.uncertain ? ", uncertain attribution" : ""}${t.overlap ? ", overlapping speech" : ""}`}
                      onClick={() => {
                        setActive(new Map(active).set(laneIdx, t.idx));
                        onSelect(t.idx);
                        player.seek(t.start_ms);
                      }}
                      className={cx(
                        "absolute top-[7px] h-3.5 min-w-[3px] cursor-pointer rounded-[3px]",
                        laneColor(speaker?.lane ?? 0, !speaker?.name),
                        t.attribution.uncertain &&
                          "opacity-70 outline-2 outline-offset-[-2px] outline-dotted outline-bg",
                        t.overlap &&
                          "bg-[repeating-linear-gradient(135deg,transparent_0_3px,rgb(255_255_255/0.55)_3px_5px)]",
                        selected === t.idx && "ring-2 ring-fg",
                        pos === activePos && "group-focus-visible/lane:ring-2 group-focus-visible/lane:ring-focus",
                      )}
                      style={{ left: pct(t.start_ms), width: pct(Math.max(t.end_ms - t.start_ms, 150)) }}
                    />
                  ))}
                </div>
              );
            })}
            {record.gaps
              .filter((g) => g.kind === "silence")
              .map((g) => (
                <span
                  key={g.start_ms}
                  aria-hidden="true"
                  title={g.label}
                  className="pointer-events-none absolute inset-y-0 bg-[repeating-linear-gradient(90deg,var(--color-divider)_0_1px,transparent_1px_4px)] opacity-80"
                  style={{ left: pct(g.start_ms), width: pct(g.end_ms - g.start_ms) }}
                />
              ))}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 w-0.5 bg-fg"
              style={{ left: pct(player.timeMs) }}
            />
            <TimeTicks ticks={ticks} pct={pct} />
          </div>
        </div>
      </div>
      <p className="sr-only">
        Speakers:{" "}
        {speakers.map((s) => `${s.display}, ${clock(s.speaking_ms)}, ${String(s.turn_count)} turns`).join("; ")}.
      </p>
    </section>
  );
}

export function KeyFacts({ record }: { record: EvidenceRecord }) {
  const k = record.key_facts;
  const rows: [string, string][] = [
    [
      "Clip",
      [
        k.clip.duration_ms === null ? null : clock(k.clip.duration_ms),
        k.clip.resolution,
        k.clip.size_bytes === null ? null : formatBytes(k.clip.size_bytes),
      ]
        .filter(Boolean)
        .join(" · ") || "Not archived",
    ],
    [
      "Speakers",
      `${String(k.speakers.detected)} detected · ${String(k.speakers.named)} named · ${String(k.speakers.unresolved)} unresolved`,
    ],
    [
      "Transcript",
      k.transcript.generated
        ? `Generated · ${languageName(k.transcript.language)} · ${String(k.transcript.turns)} turns`
        : "Not yet",
    ],
    ["Checks", checksFact(k.checks)],
    ["Translation", k.translation],
    ["Context", k.context],
  ];
  return (
    <dl className="m-0 grid grid-cols-2 gap-x-4 gap-y-1 border-t border-divider px-3 py-2.5 text-md max-[1100px]:grid-cols-1">
      {rows.map(([label, value]) => (
        <div key={label} className="flex min-w-0 gap-2">
          <dt className="w-[74px] flex-none text-muted">{label}</dt>
          <dd className="m-0 min-w-0 truncate" title={value}>
            {value}
          </dd>
        </div>
      ))}
    </dl>
  );
}
